import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { formatDateOrDash, formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';

import useDetailTable from '#/hooks/table/useDetailTable';

import WorkOrderRelatedDocActionsCell from './WorkOrderRelatedDocActionsCell';

const VARIANT_CONFIG = {
  stockIssue: {
    docNo: (row) => row?.issueNo ?? row?.documentNo ?? row?.no ?? '—',
    date: (row) => formatDateOrDash(row?.issueDate ?? row?.documentDate),
    getDetailPath: (row, workOrderId) => {
      if (!row?.id) {
        return null;
      }
      const base = ROUTES.stockIssueDetail(row.id);
      if (!workOrderId) {
        return base;
      }
      return `${base}?workOrderId=${encodeURIComponent(workOrderId)}`;
    },
  },
  stockReceipt: {
    docNo: (row) => row?.receiptNo ?? row?.documentNo ?? row?.no ?? '—',
    date: (row) => formatDateOrDash(row?.receiptDate ?? row?.documentDate),
    getDetailPath: (row) => (row?.id ? ROUTES.stockReceiptDetail(row.id) : null),
  },
  amendment: {
    docNo: (row) => row?.amendmentNo ?? row?.documentNo ?? row?.no ?? '—',
    date: (row) => formatDateTimeOrDash(row?.createdAt ?? row?.record?.createdAt),
    getDetailPath: () => null,
  },
};

function RelatedDocNoCell({ path, label }) {
  if (!path) {
    return label;
  }

  function handleDoubleClick() {
    window.open(path, '_blank', 'noopener,noreferrer');
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="link"
          tabIndex={0}
          className={cn('text-primary-600 cursor-pointer hover:underline')}
          onDoubleClick={handleDoubleClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleDoubleClick();
            }
          }}
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        雙擊開啟單據（新分頁）
      </TooltipContent>
    </Tooltip>
  );
}

RelatedDocNoCell.propTypes = {
  path: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function buildColumns(variant, { workOrderId, onMutated, enableActions }) {
  const config = VARIANT_CONFIG[variant];

  const columns = [];

  if (enableActions && (variant === 'stockIssue' || variant === 'stockReceipt')) {
    columns.push({
      id: 'actions',
      header: '操作',
      size: 168,
      enableSorting: false,
      meta: { cellClassName: 'td_left whitespace-nowrap', noTruncate: true },
      cell: ({ row }) => (
        <WorkOrderRelatedDocActionsCell
          row={row.original}
          variant={variant}
          workOrderId={workOrderId}
          onMutated={onMutated}
        />
      ),
    });
  }

  columns.push(
    {
      id: 'docNo',
      header: '單號',
      accessorFn: (row) => config.docNo(row),
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <RelatedDocNoCell
          path={config.getDetailPath(row.original, workOrderId)}
          label={config.docNo(row.original)}
        />
      ),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      enableSorting: false,
      meta: { statusPresentation: 'badge' },
      cell: ({ row }) => (
        row.original.status
          ? <DocumentStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
    },
    {
      id: 'date',
      header: variant === 'amendment' ? '建立日' : '日期',
      accessorFn: (row) => config.date(row),
      size: 130,
      enableSorting: false,
    },
  );

  if (variant === 'stockIssue') {
    columns.push({
      id: 'lineCount',
      header: '物料項目數',
      accessorFn: (row) => (
        row?.lineCount != null && row.lineCount !== ''
          ? String(row.lineCount)
          : '—'
      ),
      size: 110,
      enableSorting: false,
    });
  }

  if (variant === 'stockReceipt') {
    columns.push(
      {
        id: 'summaryBaseQty',
        header: '總數量',
        accessorFn: (row) => formatDisplayQty(row?.summaryBaseQty),
        size: 110,
        enableSorting: false,
      },
      {
        id: 'baseUnit',
        header: '單位',
        accessorFn: (row) => row?.baseUnit?.name || '—',
        size: 90,
        enableSorting: false,
      },
    );
  }

  return columns;
}

const WorkOrderRelatedDocsTable = ({
  rows = [],
  variant = 'stockIssue',
  workOrderId,
  onMutated,
}) => {
  const enableActions = Boolean(workOrderId)
    && (variant === 'stockIssue' || variant === 'stockReceipt');

  const columns = useMemo(
    () => buildColumns(variant, { workOrderId, onMutated, enableActions }),
    [variant, workOrderId, onMutated, enableActions],
  );
  const tableRows = useMemo(
    () => (rows ?? []).map((row, index) => ({
      ...row,
      _rowKey: row?.id != null && row.id !== ''
        ? `${row.id}-${index}`
        : `${variant}-${index}`,
    })),
    [rows, variant],
  );

  const detailTableState = useDetailTable(tableRows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row._rowKey),
  });

  if (tableRows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無資料</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

WorkOrderRelatedDocsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.oneOf(['stockIssue', 'stockReceipt', 'amendment']),
  workOrderId: PropTypes.string,
  onMutated: PropTypes.func,
};

export default WorkOrderRelatedDocsTable;
